// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  type ETF,
  type MutualFund,
} from '@shared/data-access/interfaces/api/ai/screener';
import { formatToMoney, numberToFixed } from '@shared/ui-kit/utils';
import { useMemo } from 'react';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import { type AssetProfileProps } from '../components/asset-profile';
import { useTranslation } from 'react-i18next';
import { i18n } from '@shared/ui-kit/providers';

type AssetInfo = MutualFund | ETF | any;

export const useAssetProfile = ({ assetType, ticker }: AssetProfileProps) => {
  const { t } = useTranslation();

  const {
    data: [data] = [] as unknown as [AssetInfo],
    isLoading,
    error,
  } = assetsApi.useAssetsInfoQuery({
    params: { detail: true },
    payload: [ticker],
    assetType,
  });

  /* istanbul ignore next */
  const infoProperties = useMemo(() => {
    return [
      {
        label: t('glossary.assets-manager', { ns: 'glossary' }) as string,
        value: data?.manager ?? data?.etf_company ?? '-',
      },
      {
        label: t('common.website', { ns: 'common' }),
        value: data?.website ? (
          <a
            href={data?.website}
            target="_blank"
            rel="noreferrer"
            className="hover:text-title-dark/80 hover:underline hover:underline-offset-4"
          >
            {data?.website}
          </a>
        ) : (
          '-'
        ),
      },
      {
        label: t('glossary.asset-class', { ns: 'glossary' }),
        value: data?.asset_class ?? '-',
      },
      {
        label: t('glossary.segment', { ns: 'glossary' }),
        value: data?.investment_segment ?? '-',
      },
      {
        label: t('glossary.benchmark-index', { ns: 'glossary' }),
        value: data?.benchmark ?? data?.tracking_index ?? '-',
      },
    ];
  }, [
    data?.asset_class,
    data?.benchmark,
    data?.etf_company,
    data?.investment_segment,
    data?.manager,
    data?.tracking_index,
    data?.website,
    t,
  ]);

  /* istanbul ignore next */
  const statisticsProperties = useMemo(
    () =>
      [
        {
          section: t('glossary.fundamentals', { ns: 'glossary' }) as string,
          isVisible: true,
          properties: [
            {
              label: t('common.isin-ticker', { ns: 'common' }) as string,
              value: data?.ticker ?? '-',
            },
            {
              label: t('common.currency', { ns: 'common' }),
              value: data?.currency ?? '-',
            },
            {
              label: t('glossary.class-assets', { ns: 'glossary' }) as string,
              value: data?.aum
                ? formatToMoney(data?.aum)
                : formatToMoney(data?.nav),
            },
          ],
        },
        {
          section: t('glossary.fundamentals', { ns: 'glossary' }),
          isVisible: assetType === 'etf',
          properties: [
            {
              label: t('glossary.pe', { ns: 'glossary' }),
              value: data?.pe ? `${numberToFixed(data.pe, 1)}` : '-',
            },
            {
              label: t('glossary.pb', { ns: 'glossary' }),
              value: data?.pb ? `${numberToFixed(data.pb, 1)}` : '-',
            },
            {
              label: t('glossary.yield', { ns: 'glossary' }),
              value: data?.dividend_yield
                ? `${numberToFixed(data.dividend_yield, 1)}%`
                : '-',
            },
          ],
        },
        {
          section: t('glossary.performance', { ns: 'glossary' }),
          isVisible: true,
          properties: [
            {
              label: t('glossary.ytd-returns', { ns: 'glossary' }),
              value: percentFormat(data?.ytd_return),
            },
            {
              label: t('glossary.1y-returns', { ns: 'glossary' }),
              value: percentFormat(data?.y1_return),
            },
            {
              label: t('glossary.3y-returns', { ns: 'glossary' }),
              value: percentFormat(data?.y3_return),
            },
            {
              label: t('glossary.5y-returns', { ns: 'glossary' }),
              value: percentFormat(data?.y5_return),
            },
          ],
        },
        {
          section: t('glossary.risk', { ns: 'glossary' }),
          isVisible: true,
          properties: [
            {
              label: t('glossary.3y-volatility', { ns: 'glossary' }) as string,
              value: percentFormat(data?.y3_vol),
            },
            {
              label: t('glossary.max-drawdown', { ns: 'glossary' }) as string,
              value: percentFormat(data?.max_draw_down),
            },
            {
              label: t('glossary.concentration-top-10', {
                ns: 'glossary',
              }) as string,
              value: percentFormat(
                data?.top_holdings?.reduce(
                  (acc: number, holding: any) => acc + holding.concentration,
                  0,
                ),
              ),
            },
          ],
        },
        {
          section: t('glossary.cost', { ns: 'glossary' }),
          isVisible: true,
          properties: [
            {
              label: t('glossary.total-expense-ratio', { ns: 'glossary' }),
              value: data?.expenses_ratio ? `${data.expenses_ratio}` : '-',
            },
          ],
        },
      ].filter(section => section.isVisible),
    [
      t,
      data?.ticker,
      data?.currency,
      data?.aum,
      data?.nav,
      data?.pe,
      data?.pb,
      data?.dividend_yield,
      data?.ytd_return,
      data?.y1_return,
      data?.y3_return,
      data?.y5_return,
      data?.y3_vol,
      data?.max_draw_down,
      data?.top_holdings,
      data?.expenses_ratio,
      assetType,
    ],
  );

  return {
    error,
    data,
    loading: isLoading,
    infoProperties,
    statisticsProperties,
  };
};
/* istanbul ignore next */
const percentFormat = (value: number | undefined) => {
  const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

  return typeof value === 'number'
    ? new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: 1,
        useGrouping: true,
      }).format(value)
    : '-';
};
